import { BoxGeometry, Group, Mesh, type Scene } from 'three';
import type { Board, Coord, TileType } from '../core/types.js';
import { materialFor } from './materials.js';
import { buildGlyphMesh } from './glyph.js';
import { buildLabelMesh } from './label.js';

export const CELL_SIZE = 1.1;
export const CELL_GAP = 0.1;
export const CELL_DEPTH = 0.85;
const STEP = CELL_SIZE + CELL_GAP;

export interface CellMeshData {
  readonly x: number;
  readonly y: number;
  readonly baseZ: number;
}

export type CellMesh = Mesh & { userData: CellMeshData };

export interface BoardMeshes {
  readonly group: Group;
  readonly cells: CellMesh[][];
  destroy(): void;
}

const cellWorldPos = (size: number, x: number, y: number): { x: number; y: number } => {
  const offset = ((size - 1) * STEP) / 2;
  return { x: x * STEP - offset, y: -(y * STEP - offset) };
};

export const cellPosition = (size: number, c: Coord): { x: number; y: number } =>
  cellWorldPos(size, c.x, c.y);

export const buildBoardMeshes = (board: Board, scene: Scene): BoardMeshes => {
  const group = new Group();
  scene.add(group);
  const geo = new BoxGeometry(CELL_SIZE, CELL_SIZE, CELL_DEPTH, 1, 1, 1);
  const cells: CellMesh[][] = [];

  const isStart = (x: number, y: number): boolean => x === board.start.x && y === board.start.y;
  const isEnd = (x: number, y: number): boolean => x === board.end.x && y === board.end.y;

  for (let y = 0; y < board.size; y++) {
    const row: CellMesh[] = [];
    for (let x = 0; x < board.size; x++) {
      const cell = board.cells[y]![x]!;
      const mesh = new Mesh(geo, materialFor(cell.color, cell.tile));
      const wp = cellWorldPos(board.size, x, y);
      const baseZ = cell.tile === 'locked' ? -0.05 : 0;
      mesh.position.set(wp.x, wp.y, baseZ);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData = { x, y, baseZ };
      if (isStart(x, y)) {
        mesh.add(buildLabelMesh('START', '#1a1d29', '#ffffff', CELL_SIZE * 0.92));
      } else if (isEnd(x, y)) {
        mesh.add(buildLabelMesh('GOAL', '#1a1d29', '#fde047', CELL_SIZE * 0.92));
      } else {
        const glyph = buildGlyphMesh(cell.tile, CELL_SIZE);
        if (glyph) mesh.add(glyph);
      }
      const cellMesh = mesh as unknown as CellMesh;
      group.add(cellMesh);
      row.push(cellMesh);
    }
    cells.push(row);
  }

  return {
    group,
    cells,
    destroy() {
      scene.remove(group);
      geo.dispose();
    },
  };
};

export const updateCellMaterial = (
  meshes: BoardMeshes,
  c: Coord,
  color: string,
  tile: TileType,
): void => {
  const m = meshes.cells[c.y]?.[c.x];
  if (!m) return;
  m.material = materialFor(color, tile);
};
